import { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/**
 * Input/Textarea con STATO LOCALE.
 *
 * Perché esiste
 * -------------
 * In pagine grandi (Occurrences.jsx 2000 righe, ExpenseForm.jsx 1500,
 * PrimaNota 1100), un `<Input value={foo} onChange={setFoo}>` classico
 * fa ri-renderizzare TUTTO il componente ad ogni carattere. Su liste
 * di 100+ righe + KPI + useMemo pesanti = **lag di secondi**.
 *
 * Questo componente:
 *  - Tiene lo stato del campo *localmente* (React.memo + useState).
 *  - Digitare è ISTANTANEO (solo l'Input si ri-renderizza).
 *  - Propaga il valore al parent solo al blur o dopo `debounceMs` di
 *    inattività (default 300ms).
 *  - Se il parent aggiorna `value` da fuori (es. apertura dialog con
 *    riga diversa), il componente si sincronizza.
 *
 * Uso
 * ---
 *   <StableInput value={form.description}
 *                onCommit={(v) => setForm(s => ({...s, description: v}))} />
 *
 *   <StableTextarea value={editNotes} onCommit={setEditNotes} rows={4} />
 *
 * Non chiama `onChange` (a differenza di Input classico) per rendere
 * chiaro che la sincronizzazione col parent è ritardata.
 */

const StableFieldBase = forwardRef(function StableFieldBase(
  { value, onCommit, debounceMs = 300, as = "input", ...rest },
  ref,
) {
  const [local, setLocal] = useState(value ?? "");
  const timerRef = useRef(null);
  const skipNextSync = useRef(false);

  // Sync verso il parent (debounce). Salta la sync se stiamo appena
  // ricevendo un update *da* fuori.
  useEffect(() => {
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (local !== (value ?? "")) onCommit?.(local);
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Ci interessa solo il cambio locale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  // Sync verso il basso quando il parent cambia `value` (es. dialog
  // riaperto su una riga diversa).
  useEffect(() => {
    if ((value ?? "") !== local) {
      skipNextSync.current = true;
      setLocal(value ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleBlur = (e) => {
    // Al blur, forza la commit immediata (senza attendere il debounce).
    if (timerRef.current) clearTimeout(timerRef.current);
    if (local !== (value ?? "")) onCommit?.(local);
    rest.onBlur?.(e);
  };

  const Component = as === "textarea" ? Textarea : Input;
  return (
    <Component
      {...rest}
      ref={ref}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={handleBlur}
    />
  );
});

export const StableInput = forwardRef(function StableInput(props, ref) {
  return <StableFieldBase {...props} as="input" ref={ref} />;
});

export const StableTextarea = forwardRef(function StableTextarea(props, ref) {
  return <StableFieldBase {...props} as="textarea" ref={ref} />;
});
